import cn from 'classnames';
import React, { FC, useRef, useState } from 'react';

import { Bars } from '@.partials';
import { Filter3, FilterData } from '@.ui';
import { analyticsStoreSelectors } from '@/Store';
import { PreferableProductsStatistic } from '@/Types/analytics';

import { BAR_FACTOR, MINI_TOP_STEP_VALUE, TOP_STEP_VALUE } from '../FavoriteHeartChart/constants';
import S from './styles.module.scss';

interface PreferableProductsChartProps {
    data: PreferableProductsStatistic;
    mini?: boolean;
}

enum FilterType {
    AGE = 'age',
    SEX = 'sex',
    STRENGTH = 'strength',
    TIME = 'time',
}

export const PreferableProductsChart: FC<PreferableProductsChartProps> = ({ data, mini = false }) => {
    const [ageIds, setAgeIds] = useState<number[]>([]);
    const [genderId, setGenderId] = useState<number | undefined>(-1);
    const [strengthId, setStrengthId] = useState<number | undefined>(-1);
    const [timeId, setTimeId] = useState<number | undefined>(-1);

    const hasInteracted = useRef(false);

    const filterPreferableProducts = analyticsStoreSelectors.use.filterPreferableProducts();

    const stepValue = mini ? MINI_TOP_STEP_VALUE : TOP_STEP_VALUE;

    const getFilterValues = (item: FilterData, filterType: FilterType) => {
        if (filterType === FilterType.AGE) {
            const newAgeIds = item.age_id && typeof item.age_id === 'string' ? item.age_id.split(',').map((id) => Number(id)) : undefined;

            if (!newAgeIds) {
                return { ageIds: undefined, genderId, strengthId, timeId };
            }

            setAgeIds(newAgeIds);

            return { ageIds: newAgeIds, genderId, strengthId, timeId };
        }

        if (filterType === FilterType.SEX) {
            const newGenderId = item.sex_id ? Number(item.sex_id) : undefined;

            setGenderId(newGenderId);

            return { ageIds, genderId: newGenderId, strengthId, timeId };
        }

        if (filterType === FilterType.STRENGTH) {
            const newStrengthId = item.strength_id ? Number(item.strength_id) : undefined;

            setStrengthId(newStrengthId);

            return { ageIds, genderId, strengthId: newStrengthId, timeId };
        }

        if (filterType === FilterType.TIME) {
            const newTimeId = item.occasion_id ? Number(item.occasion_id) : undefined;

            setTimeId(newTimeId);

            return { ageIds, genderId, strengthId, timeId: newTimeId };
        }

        return { ageIds: undefined, genderId: undefined, strengthId: undefined, timeId: undefined };
    };

    const onChangeFilter = (item: FilterData, filterType: FilterType) => {
        const hasValue = item.age_id || item.sex_id || item.strength_id || item.occasion_id;

        if (!hasInteracted.current && !hasValue) return;

        hasInteracted.current = true;
        filterPreferableProducts(getFilterValues(item, filterType));
    };

    const height = data.dataset.length * stepValue * BAR_FACTOR;

    return (
        <div className={cn(S.preferable_products, { [S['preferable_products-mini']]: mini })}>
            <h3 className={cn(S.title, { [S['title-mini']]: mini })}>Top Products Chosen by Users</h3>
            {!mini && (
                <div className={S.filtersWrapper}>
                    <Filter3
                        className={S.filter}
                        onChange={(age) => onChangeFilter(age, FilterType.AGE)}
                        fromData={{
                            ages: data.ages,
                        }}
                        isSaveInQuery={false}
                        resetOptionName="All ages"
                    >
                        <Filter3.Ages visibleLabel={''} visibleName="All ages" multiple />
                    </Filter3>
                    <Filter3
                        className={S.filter}
                        onChange={(sex) => onChangeFilter(sex, FilterType.SEX)}
                        fromData={{
                            sexes: data.genders,
                        }}
                        isSaveInQuery={false}
                        resetOptionName="All genders"
                    >
                        <Filter3.Sexes visibleLabel={''} visibleName="All genders" />
                    </Filter3>
                    <Filter3
                        className={S.filter}
                        onChange={(strength) => onChangeFilter(strength, FilterType.STRENGTH)}
                        fromData={{
                            strengths: data.strengths,
                        }}
                        isSaveInQuery={false}
                        resetOptionName="All strengths"
                    >
                        <Filter3.Strengths visibleLabel={''} visibleName="All strengths" />
                    </Filter3>
                    <Filter3
                        className={S.filter}
                        onChange={(time) => onChangeFilter(time, FilterType.TIME)}
                        fromData={{
                            occasions: data.times,
                        }}
                        isSaveInQuery={false}
                        resetOptionName="All times"
                    >
                        <Filter3.Occasions visibleLabel={''} visibleName="All times" />
                    </Filter3>
                </div>
            )}

            <Bars data={data.dataset} height={height} isHorizontal axisDataName="Products" isHideFractionalTicks mini={mini} />
        </div>
    );
};
